<script lang="ts">
  import { LogIn } from "lucide-svelte";
  import { authStore } from "../../auth/auth-store";
  import { i18n } from "../../chat/i18n.svelte";
  import logoUrl from "../../assets/vipex.png";

  const { t } = i18n;

  let username = $state("");
  let password = $state("");
  let error = $state<string | null>(null);
  let loading = $state(false);
  const usernameInputId = "auth-username";
  const passwordInputId = "auth-password";

  async function handleSubmit() {
    error = null;
    if (!username.trim() || !password.trim()) {
      error = t("usernamePasswordRequired");
      return;
    }

    loading = true;
    try {
      await authStore.login(username.trim(), password);
    } catch (e: any) {
      const msg = e.message;
      if (msg === "Invalid credentials") {
        error = t("invalidCredentials");
      } else {
        error = msg || t("somethingWentWrong");
      }
    } finally {
      loading = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") handleSubmit();
  }
</script>

<div
  class="flex flex-col items-center justify-center h-full p-6"
  style="background: var(--chat-bg); font-family: var(--chat-font-sans);"
>
  <!-- Logo / Title -->
  <div
    style="
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    margin-bottom: 28px;
  "
  >
    <div
      style="
      width: 64px; height: 64px;
      border-radius: 50%;
      background: white;
      display: flex; align-items: center; justify-content: center;
      box-shadow: var(--chat-shadow-md);
      overflow: hidden;
    "
    >
      <img
        src={logoUrl}
        alt={t("appTitle")}
        style="width: 100%; height: 100%; object-fit: cover;"
      />
    </div>
    <div style="text-align: center;">
      <div
        style="font-size: 20px; font-weight: 700; color: var(--chat-brand-green); letter-spacing: -0.01em;"
      >
        {t("appTitle")}
      </div>
      <div
        style="font-size: 14px; color: var(--chat-text-secondary); margin-top: 4px;"
      >
        {t("signInToContinue")}
      </div>
    </div>
  </div>

  <div
    style="
    width: 100%; max-width: 320px;
    background: var(--chat-bg-secondary);
    border: 1px solid var(--chat-bg-tertiary);
    border-radius: var(--chat-radius);
    padding: 28px 24px;
    display: flex; flex-direction: column; gap: 16px;
    box-shadow: var(--chat-shadow-md);
  "
  >
    <!-- Username -->
    <div style="display: flex; flex-direction: column; gap: 4px;">
      <label
        for={usernameInputId}
        style="font-size: 11px; font-weight: 600; color: var(--chat-text-secondary);"
        >{t("username")}</label
      >
      <input
        id={usernameInputId}
        type="text"
        bind:value={username}
        onkeydown={handleKeydown}
        placeholder={t("username")}
        autocomplete="username"
        style="
          padding: 10px 12px;
          font-size: 13px;
          background: var(--chat-bg);
          border: 1px solid var(--chat-bg-tertiary);
          border-radius: 8px;
          color: var(--chat-text-primary);
          outline: none;
          font-family: var(--chat-font-sans);
          transition: border-color 0.15s;
        "
        onfocus={(e) =>
          ((e.target as HTMLElement).style.borderColor = "var(--chat-accent)")}
        onblur={(e) =>
          ((e.target as HTMLElement).style.borderColor =
            "var(--chat-bg-tertiary)")}
      />
    </div>

    <!-- Password -->
    <div style="display: flex; flex-direction: column; gap: 4px;">
      <label
        for={passwordInputId}
        style="font-size: 11px; font-weight: 600; color: var(--chat-text-secondary);"
        >{t("password")}</label
      >
      <input
        id={passwordInputId}
        type="password"
        bind:value={password}
        onkeydown={handleKeydown}
        placeholder={t("password")}
        autocomplete="current-password"
        style="
          padding: 8px 10px;
          font-size: 12px;
          background: var(--chat-bg);
          border: 1px solid var(--chat-border);
          border-radius: var(--chat-radius);
          color: var(--chat-text-primary);
          outline: none;
          font-family: var(--chat-font-sans);
        "
        onfocus={(e) =>
          ((e.target as HTMLElement).style.borderColor = "var(--chat-accent)")}
        onblur={(e) =>
          ((e.target as HTMLElement).style.borderColor = "var(--chat-border)")}
      />
    </div>

    <!-- Error -->
    {#if error}
      <div
        style="
        padding: 8px 10px;
        background: rgba(241,112,122,0.1);
        border: 1px solid rgba(241,112,122,0.3);
        border-radius: var(--chat-radius);
        font-size: 11px;
        color: var(--chat-error);
      "
      >
        {error}
      </div>
    {/if}

    <!-- Submit Button -->
    <button
      type="button"
      onclick={handleSubmit}
      disabled={loading}
      style="
        display: flex; align-items: center; justify-content: center; gap: 8px;
        padding: 12px 16px;
        background: var(--chat-accent);
        color: #fff;
        border: none;
        border-radius: var(--chat-radius-pill);
        font-size: 14px;
        font-weight: 700;
        cursor: {loading ? 'not-allowed' : 'pointer'};
        opacity: {loading ? '0.7' : '1'};
        font-family: var(--chat-font-sans);
        transition: transform 0.1s, background 0.15s;
      "
      onmousedown={(e) => {
        if (!loading)
          (e.currentTarget as HTMLElement).style.transform =
            "scale(var(--button-active-scale))";
      }}
      onmouseup={(e) => {
        (e.currentTarget as HTMLElement).style.transform = "scale(1)";
      }}
    >
      <LogIn size={15} />
      {loading ? t("signingIn") : t("signIn")}
    </button>
  </div>
</div>

<style>
  input::placeholder {
    color: var(--chat-text-muted);
    opacity: 0.7;
  }
</style>
